import { useMemo } from 'react';
import type { LabTab } from '../LabShell.js';
import { getAllResults } from '../bench.js';
import { MODELS } from '../catalog.js';
import { CLOUD_REFERENCES, findCloud } from '../cloud.js';
import { recommendForMachine } from '../recommend.js';
import { planRouting } from '../router.js';
import { worthRunningLocal } from '../score.js';
import type { MachineProfile } from '../types.js';
import { Bar, TierBadge, VerdictPill } from '../ui.js';

interface OverviewTabProps {
  machine: MachineProfile;
  mode: 'beginner' | 'pro';
  onJumpTab: (tab: LabTab, slug?: string) => void;
}

export function OverviewTab({ machine, mode, onJumpTab }: OverviewTabProps) {
  const topForCoding = useMemo(() => recommendForMachine(machine, 'best-coding', 3), [machine]);
  const topFastest = useMemo(() => recommendForMachine(machine, 'fastest', 3), [machine]);
  const topBeginner = useMemo(() => recommendForMachine(machine, 'beginner', 3), [machine]);
  const results = getAllResults().filter((r) => r.machineId === machine.id);
  const plan = useMemo(() => planRouting(machine, { budget: 'standard' }), [machine]);
  const sonnet = findCloud('claude-sonnet-4-6') ?? CLOUD_REFERENCES[0];
  const headline = topForCoding[0];
  const worth = headline ? worthRunningLocal(headline.model, headline.fit, sonnet, 4) : null;

  return (
    <div className="lab-overview">
      <article className="lab-overview-banner">
        <div>
          <span className="section-kicker">Now playing</span>
          <h3>{machine.nickname}</h3>
          <p>
            {machine.ramGb}GB RAM · {machine.vramGb}GB usable · {machine.accel} · preferred runtime{' '}
            <strong>{machine.preferredRuntime ?? machine.installedRuntimes[0] ?? 'ollama'}</strong>
          </p>
        </div>
        <div className="lab-overview-cta">
          <button type="button" className="primary-button" onClick={() => onJumpTab('scan')}>
            Detect + run fit check
          </button>
          <button type="button" className="ghost-button" onClick={() => onJumpTab('bench')}>
            Benchmark this machine
          </button>
          <button type="button" className="ghost-button" onClick={() => onJumpTab('routing')}>
            See routing plan
          </button>
        </div>
      </article>

      {worth && (
        <article className={`lab-worth lab-worth-${worth.band}`}>
          <header>
            <span className="section-kicker">Worth running local?</span>
            <strong>{worth.headline}</strong>
            <span className="lab-worth-score">{worth.score}/100</span>
          </header>
          <Bar
            value={worth.score}
            max={100}
            tone={worth.band === 'yes' ? 'mint' : worth.band === 'maybe' ? 'gold' : 'coral'}
          />
          <p className="lab-worth-sub">vs. {sonnet.displayName} at 4 hours/day of coding.</p>
          <ul>
            {worth.reasons.slice(0, 3).map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
          <button type="button" className="ghost-button" onClick={() => onJumpTab('routing')}>
            Open routing plan
          </button>
        </article>
      )}

      <div className="lab-overview-grid">
        <PicksCard
          title="Best for coding"
          subtitle="Highest coding-tier model that fits."
          picks={topForCoding}
          onDetails={() => onJumpTab('scan')}
        />
        <PicksCard
          title="Fastest"
          subtitle="Highest tokens/sec with a passing fit."
          picks={topFastest}
          onDetails={() => onJumpTab('compare')}
        />
        <PicksCard
          title="Beginner pick"
          subtitle="Safe default if you are new to local."
          picks={topBeginner}
          onDetails={() => onJumpTab('scan')}
        />
      </div>

      <section className="lab-overview-routing">
        <header>
          <span className="section-kicker">Routing plan preview</span>
          <h4>What Vibetime would auto-pick per task</h4>
        </header>
        <ul>
          {plan.taskRoutes.slice(0, 6).map((r) => (
            <li key={r.task}>
              <strong>{r.task}</strong>
              <span className={`lab-decision lab-decision-${r.decision}`}>{r.decision}</span>
              <span>
                {r.localModel?.displayName ?? '—'} ↔ {r.cloudModel?.displayName ?? '—'}
              </span>
            </li>
          ))}
        </ul>
        <button type="button" className="ghost-button" onClick={() => onJumpTab('routing')}>
          Open full routing plan
        </button>
      </section>

      <div className="lab-overview-metrics">
        <article>
          <span>Catalog models</span>
          <strong>{MODELS.length}</strong>
        </article>
        <article>
          <span>Bench runs on this machine</span>
          <strong>{results.length}</strong>
        </article>
        <article>
          <span>Recommended runtime</span>
          <strong>{machine.preferredRuntime ?? 'ollama'}</strong>
        </article>
        <article>
          <span>Mode</span>
          <strong>{mode === 'beginner' ? 'Beginner (plain English)' : 'Pro (raw metrics)'}</strong>
        </article>
      </div>

      {mode === 'beginner' && (
        <article className="lab-overview-explainer">
          <h4>How this works</h4>
          <ol>
            <li>
              <strong>Scan</strong> — detect your machine from the browser, see what fits.
            </li>
            <li>
              <strong>Bench</strong> — run coding tasks against the models you care about.
            </li>
            <li>
              <strong>Compare</strong> — N local models vs. a cloud reference, side by side.
            </li>
            <li>
              <strong>Routing</strong> — what to run local vs. cloud for each task type.
            </li>
            <li>
              <strong>Directory</strong> — public, deep-linkable pages by machine, GPU, model.
            </li>
            <li>
              <strong>Receipts</strong> — every result is signed and shareable.
            </li>
          </ol>
        </article>
      )}
    </div>
  );
}

interface PicksCardProps {
  title: string;
  subtitle: string;
  picks: ReturnType<typeof recommendForMachine>;
  onDetails: () => void;
}

function PicksCard({ title, subtitle, picks, onDetails }: PicksCardProps) {
  return (
    <article className="lab-picks-card">
      <header>
        <span className="section-kicker">{title}</span>
        <p>{subtitle}</p>
      </header>
      <ul>
        {picks.map((p) => (
          <li key={p.model.id}>
            <div className="lab-pick-head">
              <strong>{p.model.displayName}</strong>
              <TierBadge tier={p.fit.codingUsefulnessTier} />
            </div>
            <div className="lab-pick-meta">
              <VerdictPill verdict={p.fit.verdict} />
              <span>{p.fit.expectedTps} tok/s</span>
              <span>{p.fit.quantization}</span>
            </div>
            <p>{p.why}</p>
          </li>
        ))}
      </ul>
      <button type="button" className="ghost-button" onClick={onDetails}>
        See details
      </button>
    </article>
  );
}
